<template>
    <div>
        <div class="row q-col-gutter-md q-mb-md">
            <div class="col-12 col-md">
                <data-widget
                    :title="$t('Data Entry Score')"
                    :value="dataEntryScore"
                    unit="%"
                    :relativeDifference="getDataEntryScoreDifference"
                    :relativeDifferenceIncreaseIsPositive="true"
                    :hasColorRange="true"
                    :colorRanges="[
                        {
                            low: 0,
                            high: 75,
                            color: 'negative'
                        },
                        {
                            low: 75,
                            high: 90,
                            color: 'warning'
                        },
                        {
                            low: 90,
                            high: 100,
                            color: 'positive'
                        }
                    ]"
                />
            </div>
            <div class="col-12 col-md">
                <data-widget
                    :title="$t('Unjustified Major Events')"
                    :value="totalUnjustifiedEvents"
                    :relativeDifference="getUnjustifiedEventsDifference"
                />
            </div>
            <div class="col-12 col-md">
                <data-widget
                    :title="$t('Total Major Events')"
                    :value="totalMajorEvents"
                    :relativeDifference="getMajorEventsComparison"
                />
            </div>
        </div>
        <data-entry-heat-map
            id="data-entry-report"
            class="heatmap"
            :facility="facility"
            :chartData="report"
            :selectedAssets="selectedAssets"
            @handleShowEvents="handleShowEvents"
        />

        <data-entry-heat-map
            id="comparison-data-entry-report"
            class="heatmap q-mt-md"
            v-if="hasComparison"
            :facility="facility"
            :chartData="comparisonReport"
            :selectedAssets="selectedAssets"
            @handleShowEvents="handleShowEvents"
        />

        <!-- Downtime events table -->
        <q-dialog v-model="showDialog" class="eventsDialog">
            <events-table
                @close="refreshReport"
                :eventsData="eventsData"
                @causeJustified="isCauseJustified = true"
            />
        </q-dialog>
    </div>
</template>
<script>
import DataEntryHeatMap from './DataEntryHeatMap.vue';
import DataEntry from './dataentry.js';
import { mapGetters } from 'vuex';
import EventsTable from '@components/EventsTable.vue';
import DataWidget from '@components/DataWidget.vue';
import DateGenerator from '@utils/dateGenerator.js';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';

export default {
    name: 'DataEntryReport',
    components: {
        DataEntryHeatMap,
        EventsTable,
        DataWidget
    },
    emits: ['refresh'],
    props: {
        facility: {
            type: Number,
            required: true
        },
        records: {
            type: Object,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        hasComparison: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            noDataLabels: {
                xAxisLabels: [this.$t('No Data Available')],
                yAxisLabels: [this.$t('No Data')]
            },

            // Downtime events list dialog
            showEventsDialog: false,
            eventsData: {
                assetId: null,
                productId: null,
                productName: null,
                planned: null,
                typeOfLoss: null,
                start: null,
                end: null,
                isMinor: null
            },

            dataEntryScore: 0,
            totalMajorEvents: 0,
            totalUnjustifiedEvents: 0,
            comparisonTotalMajorEvents: null,
            comparisonTotalUnjustifiedEvents: null,
            comparisonDataEntryScore: null,

            isCauseJustified: false
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange',
            getResolution: 'resolution'
        }),
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            if (
                this.records.currentReportData &&
                this.records.currentReportData.length > 0
            ) {
                const dataEntry = new DataEntry(
                    this.records.currentReportData,
                    this.getDateRange.start,
                    this.getDateRange.end,
                    this.getResolution,
                    this.localTimezone,
                    this.filters
                );
                dataEntry.createData();

                this.updateDataEntryValues({
                    totalMajorEvents: dataEntry.getTotalMajorEvents(),
                    totalUnjustifiedEvents:
                        dataEntry.getTotalUnjustifiedEvents(),
                    dataEntryScore: dataEntry.getDataEntryScore(),
                    isComparison: false
                });

                return {
                    chartMatrix: dataEntry.getData(),
                    xAxisLabels: dataEntry.getXAxisLabels(),
                    yAxisLabels: dataEntry.getYAxisLabels(),
                    assetMap: dataEntry.getAssetMap(),
                    dateMap: dataEntry.getDateMap(),
                    dateInterval: {
                        start: this.getDateRange.start,
                        end: this.getDateRange.end
                    }
                };
            }

            return {
                ...this.noDataLabels,
                dateInterval: {
                    start: this.getDateRange.start,
                    end: this.getDateRange.end
                }
            };
        },
        /**
         * Build report data of comparison interval
         * @return {Object} report data of comparison interval
         */
        comparisonReport() {
            if (
                this.records.comparisonReportData &&
                this.records.comparisonReportData.length > 0
            ) {
                const dataEntry = new DataEntry(
                    this.records.comparisonReportData,
                    this.getDateRange.compareStart,
                    this.getDateRange.compareEnd,
                    this.getResolution,
                    this.localTimezone,
                    this.filters
                );
                dataEntry.createData();

                this.updateDataEntryValues({
                    totalMajorEvents: dataEntry.getTotalMajorEvents(),
                    totalUnjustifiedEvents:
                        dataEntry.getTotalUnjustifiedEvents(),
                    dataEntryScore: dataEntry.getDataEntryScore(),
                    isComparison: true
                });

                return {
                    chartMatrix: dataEntry.getData(),
                    xAxisLabels: dataEntry.getXAxisLabels(),
                    yAxisLabels: dataEntry.getYAxisLabels(),
                    assetMap: dataEntry.getAssetMap(),
                    dateMap: dataEntry.getDateMap(),
                    dateInterval: {
                        start: this.getDateRange.compareStart,
                        end: this.getDateRange.compareEnd
                    }
                };
            }
            return {
                ...this.noDataLabels,
                dateInterval: {
                    start: this.getDateRange.compareStart,
                    end: this.getDateRange.compareEnd
                }
            };
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets
            };
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        },
        /**
         * Get comparison score
         * @return {Number}
         */
        getDataEntryScoreDifference() {
            if (this.comparisonDataEntryScore) {
                return this.dataEntryScore - this.comparisonDataEntryScore;
            }
            return null;
        },
        /**
         * Get comparison unjustified events
         * @return {Number}
         */
        getUnjustifiedEventsDifference() {
            if (!this.comparisonTotalUnjustifiedEvents) {
                return null;
            }
            return this.calculateChange(
                this.totalUnjustifiedEvents,
                this.comparisonTotalUnjustifiedEvents
            );
        },
        /**
         * Get comparison major events
         * @return {Number}
         */
        getMajorEventsComparison() {
            if (!this.comparisonTotalMajorEvents) {
                return null;
            }
            return this.calculateChange(
                this.totalMajorEvents,
                this.comparisonTotalMajorEvents
            );
        },
        /**
         * This is a workaround for an android bug
         * related to vue-echarts that prevents the dialog from opening.
         * For more information check the PR:
         * https://github.com/panevo/lean-frontend/pull/940
         * @return {Boolean}
         */
        showDialog: {
            get: function () {
                return this.showEventsDialog;
            },

            set: function () {
                if (this.showEventsDialog) {
                    return true;
                }
                return false;
            }
        }
    },
    methods: {
        /**
         * Format decimal number to percent.
         * @param {Number}
         * @return {String}
         */
        formatPercent(val) {
            return (val * 100).toFixed(2) + '%';
        },
        /**
         * Get change percentage between the current and the comparison total of events
         * @return {Number}
         */
        calculateChange(currentValue, comparisonValue) {
            if (comparisonValue == 0) {
                return null;
            }
            const result =
                ((currentValue - comparisonValue) / comparisonValue) * 100;
            return result;
        },
        /**
         * Handle show events dialog.
         * Note: Use Object for named parameters to avoid linting errors
         * @param {Object} row row data of the clicked row
         */
        handleShowEvents(assetId, start) {
            const startPeriod = new Date(start);

            // Calculate end date based on the resolution
            const dateGenerator = new DateGenerator(
                this.getResolution
            ).getDateGenerator();
            const endPeriod = dateGenerator(new Date(start));

            // set URL query values to be used by event dialog
            this.eventsData = {
                groupBy: 'causes',
                assetId: assetId,
                productId: undefined,
                productName: undefined,
                name: 'Other (Major)',
                id: null,
                typeOfLoss: null,
                planned: null,
                start: startPeriod.toISOString(),
                end: endPeriod.toISOString(),
                isMinor: false
            };
            // show events dialog
            this.showEventsDialog = true;
        },
        /**
         * Refresh the report after justifying causes
         */
        refreshReport() {
            this.showEventsDialog = false;
            // Refresh the report only if a cause was justified
            if (this.isCauseJustified) {
                this.$emit('refresh');
            }
        },
        /**
         * Update data entry values
         * @param {Number} totalMajorEvents
         * @param {Number} totalUnjustifiedEvents
         * @param {Number} dataEntryScore
         * @param {Boolean} isComparison
         */
        updateDataEntryValues({
            totalMajorEvents,
            totalUnjustifiedEvents,
            dataEntryScore,
            isComparison
        }) {
            if (isComparison) {
                this.comparisonTotalMajorEvents = totalMajorEvents;
                this.comparisonTotalUnjustifiedEvents = totalUnjustifiedEvents;
                this.comparisonDataEntryScore = dataEntryScore;
            } else {
                this.totalMajorEvents = totalMajorEvents;
                this.totalUnjustifiedEvents = totalUnjustifiedEvents;
                this.dataEntryScore = dataEntryScore;
            }
        }
    }
};
</script>
<style scoped>
.heatmap {
    z-index: 0;
}
.eventsDialog {
    z-index: 1000;
}
</style>
